import { locales, type Locale } from "./homepage-seo.ts";

const en = {
  eyebrow: "Strategy research",
  title: "Research workspace",
  subtitle: "Keep hypotheses, immutable runs, and honest comparisons together before risking capital.",
  newWorkspace: "New workspace",
  workspaceName: "Workspace name",
  hypothesis: "Hypothesis",
  objective: "Primary objective",
  minTrades: "Minimum trades",
  tags: "Tags (optional)",
  tagsPlaceholder: "trend, BTC, 1h",
  create: "Create workspace",
  cancel: "Cancel",
  objectiveExpectancy: "Increase expectancy",
  objectiveDrawdown: "Reduce drawdown",
  objectiveProfitFactor: "Increase profit factor",
  metricProfitFactor: "Profit factor",
  metricExpectancy: "Expectancy",
  objectiveStability: "Improve stability",
  loading: "Loading research…",
  loadError: "Research could not be loaded on this device.",
  retry: "Retry",
  saveError: "Your change was not saved. Nothing in history was changed.",
  required: "Add both a name and a testable hypothesis.",
  workspaces: "Workspaces",
  unassigned: "Unassigned runs",
  noWorkspaces: "No research workspace yet.",
  noRuns: "No runs in this view.",
  runCount: "{count} runs",
  lastUpdated: "Updated {date}",
  localData: "Research is saved for this account on this device. Original history stays unchanged.",
  baseline: "Baseline",
  setBaseline: "Set as baseline",
  temporaryReference: "The first selected run is a temporary reference until you set a baseline.",
  selectRun: "Select run for comparison",
  selectedCount: "{count} selected",
  maxFour: "Compare up to four runs at a time.",
  selectTwo: "Select two to four runs to compare.",
  includeBaseline: "Select the saved baseline to use it as the comparison reference.",
  assignTo: "Assign selected runs to",
  assign: "Assign runs",
  assigned: "Runs assigned without changing the original results.",
  chooseWorkspace: "Choose a workspace",
  openResult: "Open result",
  openRunDetail: "Open research run",
  closeRunDetail: "Close research run",
  moreActions: "More",
  duplicateAndChange: "Duplicate & change",
  preparePaper: "Prepare for paper",
  openFullResult: "Open full result",
  runDetails: "Research run details",
  unavailable: "The original result is unavailable on this device; its immutable link is preserved.",
  comparison: "Comparison",
  exact: "Exact match",
  controlled: "Controlled comparison",
  exploratory: "Exploratory comparison",
  limited: "Limited evidence",
  notComparable: "Not comparable",
  exactHelp: "The recorded environment and settings match. Metric differences should be zero unless result data differs.",
  controlledHelp: "One recorded variable changed across candidates. This supports a focused comparison, not a causal claim.",
  exploratoryHelp: "Several variables changed. Use the result to form a new hypothesis, not to identify one cause.",
  limitedHelp: "Legacy provenance is missing. Metrics are shown, but the comparison cannot be fully verified.",
  notComparableHelp: "The market data or test environment differs. Ranking performance would be misleading.",
  changedFields: "Recorded fields that changed",
  missingFields: "Evidence not recorded",
  metricsHidden: "Metric ranking is hidden because these runs are not comparable.",
  export: "Export evidence",
  exportJSON: "JSON with methodology",
  exportCSV: "Metrics CSV",
  exportReport: "Printable report",
  exportDone: "Research export downloaded.",
  exportError: "The export could not be created.",
  riskNote: "Historical simulations are descriptive evidence, not a forecast or investment advice.",
  statusDraft: "Draft",
  statusTesting: "Testing",
  statusPaper: "Paper monitoring",
  statusArchived: "Archived",
  evidenceNotEvaluated: "Not evaluated",
  evidenceInSample: "In-sample",
  evidenceSelection: "Selection passed",
  evidenceHoldoutPass: "Locked holdout passed",
  evidenceHoldoutFail: "Locked holdout failed",
  evidenceInsufficient: "Insufficient evidence",
} as const;

export type ResearchTranslationKey = keyof typeof en;
type ResearchLocaleStrings = Record<ResearchTranslationKey, string>;

export const researchTranslations: Record<Locale, ResearchLocaleStrings> = {
  en,
  vi: {
    eyebrow: "Nghiên cứu chiến lược",
    title: "Không gian nghiên cứu",
    subtitle: "Lưu giả thuyết, các lần chạy bất biến và so sánh trung thực trước khi mạo hiểm vốn.",
    newWorkspace: "Không gian mới",
    workspaceName: "Tên không gian",
    hypothesis: "Giả thuyết",
    objective: "Mục tiêu chính",
    minTrades: "Số lệnh tối thiểu",
    tags: "Thẻ (không bắt buộc)",
    tagsPlaceholder: "xu hướng, BTC, 1h",
    create: "Tạo không gian",
    cancel: "Hủy",
    objectiveExpectancy: "Tăng kỳ vọng lợi nhuận",
    objectiveDrawdown: "Giảm sụt giảm",
    objectiveProfitFactor: "Tăng hệ số lợi nhuận",
    metricProfitFactor: "Hệ số lợi nhuận",
    metricExpectancy: "Kỳ vọng",
    objectiveStability: "Cải thiện độ ổn định",
    loading: "Đang tải nghiên cứu…",
    loadError: "Không thể tải dữ liệu nghiên cứu trên thiết bị này.",
    retry: "Thử lại",
    saveError: "Thay đổi chưa được lưu. Lịch sử không bị chỉnh sửa.",
    required: "Hãy nhập tên và một giả thuyết có thể kiểm chứng.",
    workspaces: "Không gian",
    unassigned: "Lần chạy chưa phân nhóm",
    noWorkspaces: "Chưa có không gian nghiên cứu.",
    noRuns: "Chưa có lần chạy trong mục này.",
    runCount: "{count} lần chạy",
    lastUpdated: "Cập nhật {date}",
    localData: "Nghiên cứu được lưu cho tài khoản này trên thiết bị. Lịch sử gốc không thay đổi.",
    baseline: "Mốc chuẩn",
    setBaseline: "Đặt làm mốc chuẩn",
    temporaryReference: "Lần chạy được chọn đầu tiên là mốc tạm thời cho đến khi bạn đặt mốc chuẩn.",
    selectRun: "Chọn lần chạy để so sánh",
    selectedCount: "Đã chọn {count}",
    maxFour: "Mỗi lần có thể so sánh tối đa bốn lần chạy.",
    selectTwo: "Chọn từ hai đến bốn lần chạy để so sánh.",
    includeBaseline: "Chọn mốc chuẩn đã lưu để dùng làm tham chiếu so sánh.",
    assignTo: "Gán các lần chạy đã chọn vào",
    assign: "Gán lần chạy",
    assigned: "Đã gán lần chạy mà không thay đổi kết quả gốc.",
    chooseWorkspace: "Chọn không gian",
    openResult: "Mở kết quả",
    openRunDetail: "Mở lần chạy nghiên cứu",
    closeRunDetail: "Đóng lần chạy nghiên cứu",
    moreActions: "Thêm",
    duplicateAndChange: "Nhân bản và chỉnh sửa",
    preparePaper: "Chuẩn bị chạy paper",
    openFullResult: "Mở kết quả đầy đủ",
    runDetails: "Chi tiết lần chạy nghiên cứu",
    unavailable: "Kết quả gốc không có trên thiết bị này; liên kết bất biến vẫn được giữ lại.",
    comparison: "So sánh",
    exact: "Khớp hoàn toàn",
    controlled: "So sánh có kiểm soát",
    exploratory: "So sánh khám phá",
    limited: "Bằng chứng hạn chế",
    notComparable: "Không thể so sánh",
    exactHelp: "Môi trường và thiết lập đã ghi nhận giống nhau. Chỉ số chỉ nên khác khi dữ liệu kết quả khác.",
    controlledHelp: "Một biến đã ghi nhận thay đổi giữa các ứng viên. Đây là so sánh tập trung, không phải kết luận nhân quả.",
    exploratoryHelp: "Nhiều biến đã thay đổi. Dùng kết quả để tạo giả thuyết mới, không dùng để quy về một nguyên nhân.",
    limitedHelp: "Thiếu nguồn gốc của dữ liệu cũ. Chỉ số vẫn hiển thị nhưng chưa thể xác minh đầy đủ phép so sánh.",
    notComparableHelp: "Dữ liệu thị trường hoặc môi trường kiểm thử khác nhau. Xếp hạng hiệu suất sẽ gây hiểu lầm.",
    changedFields: "Các trường đã ghi nhận có thay đổi",
    missingFields: "Bằng chứng chưa được ghi nhận",
    metricsHidden: "Đã ẩn xếp hạng chỉ số vì các lần chạy này không thể so sánh.",
    export: "Xuất bằng chứng",
    exportJSON: "JSON kèm phương pháp",
    exportCSV: "CSV chỉ số",
    exportReport: "Báo cáo để in",
    exportDone: "Đã tải tệp nghiên cứu.",
    exportError: "Không thể tạo tệp xuất.",
    riskNote: "Mô phỏng lịch sử là bằng chứng mô tả, không phải dự báo hay lời khuyên đầu tư.",
    statusDraft: "Bản nháp",
    statusTesting: "Đang kiểm thử",
    statusPaper: "Theo dõi paper",
    statusArchived: "Đã lưu trữ",
    evidenceNotEvaluated: "Chưa đánh giá",
    evidenceInSample: "Trong mẫu",
    evidenceSelection: "Đã qua vòng chọn",
    evidenceHoldoutPass: "Đã đạt holdout khóa",
    evidenceHoldoutFail: "Không đạt holdout khóa",
    evidenceInsufficient: "Chưa đủ bằng chứng",
  },
  hi: {
    eyebrow: "रणनीति शोध",
    title: "रिसर्च वर्कस्पेस",
    subtitle: "पूंजी जोखिम में डालने से पहले hypotheses, immutable runs और ईमानदार comparisons साथ रखें।",
    newWorkspace: "नया वर्कस्पेस",
    workspaceName: "वर्कस्पेस का नाम",
    hypothesis: "परिकल्पना",
    objective: "मुख्य लक्ष्य",
    minTrades: "न्यूनतम ट्रेड",
    tags: "टैग (वैकल्पिक)",
    tagsPlaceholder: "trend, BTC, 1h",
    create: "वर्कस्पेस बनाएं",
    cancel: "रद्द करें",
    objectiveExpectancy: "अपेक्षित लाभ बढ़ाएं",
    objectiveDrawdown: "ड्रॉडाउन घटाएं",
    objectiveProfitFactor: "प्रॉफिट फैक्टर बढ़ाएं",
    metricProfitFactor: "लाभ गुणक",
    metricExpectancy: "प्रत्याशा",
    objectiveStability: "स्थिरता सुधारें",
    loading: "रिसर्च लोड हो रही है…",
    loadError: "इस डिवाइस पर रिसर्च लोड नहीं हो सकी।",
    retry: "फिर कोशिश करें",
    saveError: "बदलाव सेव नहीं हुआ। इतिहास में कुछ नहीं बदला।",
    required: "नाम और जांची जा सकने वाली परिकल्पना दोनों जोड़ें।",
    workspaces: "वर्कस्पेस",
    unassigned: "बिना समूह वाले रन",
    noWorkspaces: "अभी कोई रिसर्च वर्कस्पेस नहीं है।",
    noRuns: "इस दृश्य में कोई रन नहीं है।",
    runCount: "{count} रन",
    lastUpdated: "{date} को अपडेट",
    localData: "रिसर्च इस डिवाइस पर इस खाते के लिए सेव है। मूल इतिहास नहीं बदलता।",
    baseline: "बेसलाइन",
    setBaseline: "बेसलाइन बनाएं",
    temporaryReference: "बेसलाइन सेट होने तक पहला चुना रन अस्थायी संदर्भ है।",
    selectRun: "तुलना के लिए रन चुनें",
    selectedCount: "{count} चुने गए",
    maxFour: "एक बार में अधिकतम चार रन की तुलना करें।",
    selectTwo: "तुलना के लिए दो से चार रन चुनें।",
    includeBaseline: "सेव बेसलाइन को तुलना संदर्भ बनाने के लिए चुनें।",
    assignTo: "चुने रन यहां रखें",
    assign: "रन असाइन करें",
    assigned: "मूल परिणाम बदले बिना रन असाइन हो गए।",
    chooseWorkspace: "वर्कस्पेस चुनें",
    openResult: "परिणाम खोलें",
    openRunDetail: "रिसर्च रन खोलें",
    closeRunDetail: "रिसर्च रन बंद करें",
    moreActions: "और विकल्प",
    duplicateAndChange: "कॉपी करके बदलें",
    preparePaper: "पेपर के लिए तैयार करें",
    openFullResult: "पूरा परिणाम खोलें",
    runDetails: "रिसर्च रन का विवरण",
    unavailable: "मूल परिणाम इस डिवाइस पर उपलब्ध नहीं है; उसका immutable link सुरक्षित है।",
    comparison: "तुलना",
    exact: "पूरी तरह समान",
    controlled: "नियंत्रित तुलना",
    exploratory: "खोजपरक तुलना",
    limited: "सीमित प्रमाण",
    notComparable: "तुलना योग्य नहीं",
    exactHelp: "दर्ज वातावरण और सेटिंग समान हैं। परिणाम डेटा अलग न हो तो metrics भी समान होने चाहिए।",
    controlledHelp: "उम्मीदवारों में एक दर्ज variable बदला। यह focused comparison है, causal proof नहीं।",
    exploratoryHelp: "कई variables बदले हैं। नतीजे से नई hypothesis बनाएं, एक कारण तय न करें।",
    limitedHelp: "पुराने run की provenance अधूरी है। Metrics दिखते हैं, पर comparison पूरी तरह verify नहीं हो सकता।",
    notComparableHelp: "Market data या test environment अलग है। Performance ranking भ्रामक होगी।",
    changedFields: "बदले हुए दर्ज fields",
    missingFields: "दर्ज न हुआ प्रमाण",
    metricsHidden: "Runs comparable न होने के कारण metric ranking छिपी है।",
    export: "प्रमाण एक्सपोर्ट करें",
    exportJSON: "Methodology सहित JSON",
    exportCSV: "Metrics CSV",
    exportReport: "प्रिंट योग्य रिपोर्ट",
    exportDone: "रिसर्च एक्सपोर्ट डाउनलोड हुआ।",
    exportError: "एक्सपोर्ट नहीं बन सका।",
    riskNote: "Historical simulation वर्णनात्मक प्रमाण है, forecast या investment advice नहीं।",
    statusDraft: "ड्राफ्ट",
    statusTesting: "टेस्टिंग",
    statusPaper: "पेपर मॉनिटरिंग",
    statusArchived: "आर्काइव",
    evidenceNotEvaluated: "मूल्यांकन नहीं हुआ",
    evidenceInSample: "इन-सैंपल",
    evidenceSelection: "चयन पास",
    evidenceHoldoutPass: "लॉक्ड holdout पास",
    evidenceHoldoutFail: "लॉक्ड holdout असफल",
    evidenceInsufficient: "प्रमाण अपर्याप्त",
  },
  ur: {
    eyebrow: "حکمتِ عملی کی تحقیق",
    title: "ریسرچ ورک اسپیس",
    subtitle: "سرمایہ خطرے میں ڈالنے سے پہلے مفروضے، ناقابلِ تبدیلی runs اور دیانت دار comparisons ایک جگہ رکھیں۔",
    newWorkspace: "نیا ورک اسپیس",
    workspaceName: "ورک اسپیس کا نام",
    hypothesis: "مفروضہ",
    objective: "بنیادی مقصد",
    minTrades: "کم از کم trades",
    tags: "ٹیگز (اختیاری)",
    tagsPlaceholder: "trend, BTC, 1h",
    create: "ورک اسپیس بنائیں",
    cancel: "منسوخ کریں",
    objectiveExpectancy: "متوقع منافع بڑھائیں",
    objectiveDrawdown: "drawdown کم کریں",
    objectiveProfitFactor: "profit factor بڑھائیں",
    metricProfitFactor: "منافع کا تناسب",
    metricExpectancy: "متوقع قدر",
    objectiveStability: "استحکام بہتر کریں",
    loading: "ریسرچ لوڈ ہو رہی ہے…",
    loadError: "اس ڈیوائس پر ریسرچ لوڈ نہیں ہو سکی۔",
    retry: "دوبارہ کوشش کریں",
    saveError: "تبدیلی محفوظ نہیں ہوئی۔ History میں کچھ نہیں بدلا۔",
    required: "نام اور قابلِ آزمائش مفروضہ دونوں درج کریں۔",
    workspaces: "ورک اسپیسز",
    unassigned: "غیر منسلک runs",
    noWorkspaces: "ابھی کوئی ریسرچ ورک اسپیس نہیں۔",
    noRuns: "اس منظر میں کوئی run نہیں۔",
    runCount: "{count} runs",
    lastUpdated: "{date} کو اپ ڈیٹ ہوا",
    localData: "ریسرچ اس ڈیوائس پر اس اکاؤنٹ کے لیے محفوظ ہے۔ اصل history تبدیل نہیں ہوتی۔",
    baseline: "بیس لائن",
    setBaseline: "بیس لائن بنائیں",
    temporaryReference: "بیس لائن مقرر ہونے تک پہلا منتخب run عارضی حوالہ ہے۔",
    selectRun: "موازنے کے لیے run منتخب کریں",
    selectedCount: "{count} منتخب",
    maxFour: "ایک وقت میں زیادہ سے زیادہ چار runs کا موازنہ کریں۔",
    selectTwo: "موازنے کے لیے دو سے چار runs منتخب کریں۔",
    includeBaseline: "محفوظ baseline کو موازنے کا حوالہ بنانے کے لیے منتخب کریں۔",
    assignTo: "منتخب runs یہاں شامل کریں",
    assign: "Runs شامل کریں",
    assigned: "اصل نتائج بدلے بغیر runs شامل ہو گئے۔",
    chooseWorkspace: "ورک اسپیس منتخب کریں",
    openResult: "نتیجہ کھولیں",
    openRunDetail: "ریسرچ رن کھولیں",
    closeRunDetail: "ریسرچ رن بند کریں",
    moreActions: "مزید",
    duplicateAndChange: "نقل بنا کر تبدیل کریں",
    preparePaper: "پیپر کے لیے تیار کریں",
    openFullResult: "مکمل نتیجہ کھولیں",
    runDetails: "ریسرچ رن کی تفصیل",
    unavailable: "اصل نتیجہ اس ڈیوائس پر دستیاب نہیں؛ اس کا ناقابلِ تبدیلی link محفوظ ہے۔",
    comparison: "موازنہ",
    exact: "بالکل یکساں",
    controlled: "کنٹرول شدہ موازنہ",
    exploratory: "تجرباتی موازنہ",
    limited: "محدود ثبوت",
    notComparable: "قابلِ موازنہ نہیں",
    exactHelp: "محفوظ ماحول اور settings یکساں ہیں۔ Result data مختلف نہ ہو تو metrics بھی یکساں ہونے چاہئیں۔",
    controlledHelp: "امیدواروں میں ایک محفوظ variable بدلا ہے۔ یہ focused comparison ہے، causal proof نہیں۔",
    exploratoryHelp: "کئی variables بدلے ہیں۔ نتیجے سے نیا مفروضہ بنائیں، ایک سبب طے نہ کریں۔",
    limitedHelp: "پرانے run کی provenance نامکمل ہے۔ Metrics نظر آتے ہیں مگر comparison پوری طرح verify نہیں ہو سکتا۔",
    notComparableHelp: "Market data یا test environment مختلف ہے۔ Performance ranking گمراہ کن ہوگی۔",
    changedFields: "تبدیل شدہ محفوظ fields",
    missingFields: "غیر محفوظ ثبوت",
    metricsHidden: "Runs قابلِ موازنہ نہ ہونے کی وجہ سے metric ranking چھپی ہے۔",
    export: "ثبوت ایکسپورٹ کریں",
    exportJSON: "Methodology کے ساتھ JSON",
    exportCSV: "Metrics CSV",
    exportReport: "قابلِ پرنٹ رپورٹ",
    exportDone: "ریسرچ ایکسپورٹ ڈاؤن لوڈ ہو گئی۔",
    exportError: "ایکسپورٹ تیار نہیں ہو سکی۔",
    riskNote: "Historical simulation صرف وضاحتی ثبوت ہے، forecast یا investment advice نہیں۔",
    statusDraft: "ڈرافٹ",
    statusTesting: "ٹیسٹنگ",
    statusPaper: "پیپر مانیٹرنگ",
    statusArchived: "آرکائیو",
    evidenceNotEvaluated: "جانچ نہیں ہوئی",
    evidenceInSample: "In-sample",
    evidenceSelection: "Selection پاس",
    evidenceHoldoutPass: "Locked holdout پاس",
    evidenceHoldoutFail: "Locked holdout ناکام",
    evidenceInsufficient: "ثبوت ناکافی",
  },
  th: {
    eyebrow: "วิจัยกลยุทธ์",
    title: "พื้นที่วิจัย",
    subtitle: "เก็บสมมติฐาน ผลการรันที่แก้ไขไม่ได้ และการเปรียบเทียบที่ตรงไปตรงมาก่อนเสี่ยงเงินจริง",
    newWorkspace: "พื้นที่ใหม่",
    workspaceName: "ชื่อพื้นที่",
    hypothesis: "สมมติฐาน",
    objective: "เป้าหมายหลัก",
    minTrades: "จำนวนเทรดขั้นต่ำ",
    tags: "แท็ก (ไม่บังคับ)",
    tagsPlaceholder: "trend, BTC, 1h",
    create: "สร้างพื้นที่",
    cancel: "ยกเลิก",
    objectiveExpectancy: "เพิ่มค่าคาดหวัง",
    objectiveDrawdown: "ลด drawdown",
    objectiveProfitFactor: "เพิ่ม profit factor",
    metricProfitFactor: "อัตรากำไร",
    metricExpectancy: "ค่าคาดหวัง",
    objectiveStability: "เพิ่มความสม่ำเสมอ",
    loading: "กำลังโหลดงานวิจัย…",
    loadError: "โหลดงานวิจัยบนอุปกรณ์นี้ไม่ได้",
    retry: "ลองอีกครั้ง",
    saveError: "ยังไม่ได้บันทึกการเปลี่ยนแปลง และประวัติเดิมไม่ถูกแก้ไข",
    required: "กรอกทั้งชื่อและสมมติฐานที่ทดสอบได้",
    workspaces: "พื้นที่วิจัย",
    unassigned: "ผลการรันที่ยังไม่จัดกลุ่ม",
    noWorkspaces: "ยังไม่มีพื้นที่วิจัย",
    noRuns: "ไม่มีผลการรันในมุมมองนี้",
    runCount: "{count} ครั้ง",
    lastUpdated: "อัปเดต {date}",
    localData: "งานวิจัยบันทึกสำหรับบัญชีนี้บนอุปกรณ์ โดยไม่แก้ไขประวัติต้นฉบับ",
    baseline: "ค่าฐาน",
    setBaseline: "ตั้งเป็นค่าฐาน",
    temporaryReference: "ผลการรันแรกที่เลือกจะเป็นค่าอ้างอิงชั่วคราวจนกว่าจะตั้งค่าฐาน",
    selectRun: "เลือกผลการรันเพื่อเปรียบเทียบ",
    selectedCount: "เลือกแล้ว {count}",
    maxFour: "เปรียบเทียบได้สูงสุดครั้งละสี่ผลการรัน",
    selectTwo: "เลือกสองถึงสี่ผลการรันเพื่อเปรียบเทียบ",
    includeBaseline: "เลือกค่าฐานที่บันทึกไว้เพื่อใช้เป็นตัวอ้างอิง",
    assignTo: "ย้ายผลการรันที่เลือกไปยัง",
    assign: "จัดกลุ่มผลการรัน",
    assigned: "จัดกลุ่มแล้วโดยไม่เปลี่ยนผลลัพธ์ต้นฉบับ",
    chooseWorkspace: "เลือกพื้นที่วิจัย",
    openResult: "เปิดผลลัพธ์",
    openRunDetail: "เปิดรอบวิจัย",
    closeRunDetail: "ปิดรอบวิจัย",
    moreActions: "เพิ่มเติม",
    duplicateAndChange: "ทำสำเนาและแก้ไข",
    preparePaper: "เตรียมสำหรับ Paper",
    openFullResult: "เปิดผลลัพธ์ฉบับเต็ม",
    runDetails: "รายละเอียดรอบวิจัย",
    unavailable: "ไม่พบผลลัพธ์ต้นฉบับบนอุปกรณ์นี้ แต่ยังเก็บลิงก์ที่แก้ไขไม่ได้ไว้",
    comparison: "การเปรียบเทียบ",
    exact: "ตรงกันทุกค่า",
    controlled: "เปรียบเทียบแบบควบคุม",
    exploratory: "เปรียบเทียบเพื่อสำรวจ",
    limited: "หลักฐานจำกัด",
    notComparable: "เปรียบเทียบไม่ได้",
    exactHelp: "สภาพแวดล้อมและการตั้งค่าที่บันทึกไว้ตรงกัน ค่าชี้วัดควรเท่ากันหากข้อมูลผลลัพธ์ไม่ต่างกัน",
    controlledHelp: "มีตัวแปรที่บันทึกไว้เปลี่ยนหนึ่งตัว จึงใช้เปรียบเทียบแบบเจาะจงได้ แต่ยังสรุปเหตุและผลไม่ได้",
    exploratoryHelp: "มีหลายตัวแปรเปลี่ยน ใช้ผลลัพธ์ตั้งสมมติฐานใหม่ ไม่ควรระบุสาเหตุเดียว",
    limitedHelp: "ข้อมูลแหล่งที่มาของผลเก่าไม่ครบ จึงแสดงค่าชี้วัดได้แต่ยังยืนยันการเปรียบเทียบทั้งหมดไม่ได้",
    notComparableHelp: "ข้อมูลตลาดหรือสภาพแวดล้อมทดสอบต่างกัน การจัดอันดับผลตอบแทนอาจทำให้เข้าใจผิด",
    changedFields: "ฟิลด์ที่บันทึกไว้ซึ่งเปลี่ยนแปลง",
    missingFields: "หลักฐานที่ไม่ได้บันทึก",
    metricsHidden: "ซ่อนการจัดอันดับค่าชี้วัดเพราะผลการรันเหล่านี้เปรียบเทียบกันไม่ได้",
    export: "ส่งออกหลักฐาน",
    exportJSON: "JSON พร้อมวิธีการ",
    exportCSV: "CSV ค่าชี้วัด",
    exportReport: "รายงานพร้อมพิมพ์",
    exportDone: "ดาวน์โหลดไฟล์งานวิจัยแล้ว",
    exportError: "สร้างไฟล์ส่งออกไม่ได้",
    riskNote: "การจำลองย้อนหลังเป็นหลักฐานเชิงบรรยาย ไม่ใช่การคาดการณ์หรือคำแนะนำการลงทุน",
    statusDraft: "ฉบับร่าง",
    statusTesting: "กำลังทดสอบ",
    statusPaper: "ติดตามแบบ paper",
    statusArchived: "เก็บถาวร",
    evidenceNotEvaluated: "ยังไม่ประเมิน",
    evidenceInSample: "ในกลุ่มตัวอย่าง",
    evidenceSelection: "ผ่านรอบคัดเลือก",
    evidenceHoldoutPass: "ผ่าน locked holdout",
    evidenceHoldoutFail: "ไม่ผ่าน locked holdout",
    evidenceInsufficient: "หลักฐานไม่เพียงพอ",
  },
  bn: {
    eyebrow: "কৌশল গবেষণা",
    title: "রিসার্চ ওয়ার্কস্পেস",
    subtitle: "মূলধন ঝুঁকিতে দেওয়ার আগে hypothesis, অপরিবর্তনীয় run এবং সৎ comparison একসঙ্গে রাখুন।",
    newWorkspace: "নতুন ওয়ার্কস্পেস",
    workspaceName: "ওয়ার্কস্পেসের নাম",
    hypothesis: "অনুমান",
    objective: "প্রধান লক্ষ্য",
    minTrades: "ন্যূনতম ট্রেড",
    tags: "ট্যাগ (ঐচ্ছিক)",
    tagsPlaceholder: "trend, BTC, 1h",
    create: "ওয়ার্কস্পেস তৈরি করুন",
    cancel: "বাতিল",
    objectiveExpectancy: "প্রত্যাশিত মুনাফা বাড়ান",
    objectiveDrawdown: "ড্রডাউন কমান",
    objectiveProfitFactor: "প্রফিট ফ্যাক্টর বাড়ান",
    metricProfitFactor: "প্রফিট ফ্যাক্টর",
    metricExpectancy: "প্রত্যাশিত ফল",
    objectiveStability: "স্থিতিশীলতা উন্নত করুন",
    loading: "গবেষণা লোড হচ্ছে…",
    loadError: "এই ডিভাইসে গবেষণা লোড করা যায়নি।",
    retry: "আবার চেষ্টা করুন",
    saveError: "পরিবর্তনটি সংরক্ষিত হয়নি। ইতিহাসে কিছু বদলায়নি।",
    required: "একটি নাম ও পরীক্ষাযোগ্য hypothesis দুটিই লিখুন।",
    workspaces: "ওয়ার্কস্পেস",
    unassigned: "গ্রুপবিহীন run",
    noWorkspaces: "এখনও কোনো রিসার্চ ওয়ার্কস্পেস নেই।",
    noRuns: "এই অংশে কোনো run নেই।",
    runCount: "{count}টি run",
    lastUpdated: "{date}-এ আপডেট",
    localData: "এই অ্যাকাউন্টের গবেষণা এই ডিভাইসে সংরক্ষিত। মূল ইতিহাস অপরিবর্তিত থাকে।",
    baseline: "বেসলাইন",
    setBaseline: "বেসলাইন করুন",
    temporaryReference: "বেসলাইন নির্ধারণ না করা পর্যন্ত প্রথম নির্বাচিত run অস্থায়ী রেফারেন্স।",
    selectRun: "তুলনার জন্য run নির্বাচন করুন",
    selectedCount: "{count}টি নির্বাচিত",
    maxFour: "একবারে সর্বোচ্চ চারটি run তুলনা করুন।",
    selectTwo: "তুলনার জন্য দুই থেকে চারটি run নির্বাচন করুন।",
    includeBaseline: "সংরক্ষিত baseline-কে রেফারেন্স করতে সেটি নির্বাচন করুন।",
    assignTo: "নির্বাচিত run এখানে দিন",
    assign: "Run নির্ধারণ করুন",
    assigned: "মূল ফল না বদলিয়ে run নির্ধারণ করা হয়েছে।",
    chooseWorkspace: "ওয়ার্কস্পেস নির্বাচন করুন",
    openResult: "ফল খুলুন",
    openRunDetail: "রিসার্চ রান খুলুন",
    closeRunDetail: "রিসার্চ রান বন্ধ করুন",
    moreActions: "আরও",
    duplicateAndChange: "অনুলিপি করে পরিবর্তন করুন",
    preparePaper: "পেপারের জন্য প্রস্তুত করুন",
    openFullResult: "সম্পূর্ণ ফল খুলুন",
    runDetails: "রিসার্চ রানের বিবরণ",
    unavailable: "মূল ফলটি এই ডিভাইসে নেই; তার অপরিবর্তনীয় link সংরক্ষিত আছে।",
    comparison: "তুলনা",
    exact: "সম্পূর্ণ মিল",
    controlled: "নিয়ন্ত্রিত তুলনা",
    exploratory: "অনুসন্ধানমূলক তুলনা",
    limited: "সীমিত প্রমাণ",
    notComparable: "তুলনাযোগ্য নয়",
    exactHelp: "রেকর্ড করা পরিবেশ ও setting মিলে গেছে। Result data না বদলালে metric-ও একই হওয়া উচিত।",
    controlledHelp: "Candidate-গুলোর মধ্যে একটি রেকর্ড করা variable বদলেছে। এটি focused comparison, causal proof নয়।",
    exploratoryHelp: "একাধিক variable বদলেছে। ফল থেকে নতুন hypothesis বানান, একক কারণ নির্ধারণ করবেন না।",
    limitedHelp: "পুরোনো run-এর provenance অসম্পূর্ণ। Metric দেখা যাবে, তবে comparison পুরোপুরি যাচাই করা যাবে না।",
    notComparableHelp: "Market data বা test environment আলাদা। Performance ranking বিভ্রান্তিকর হবে।",
    changedFields: "পরিবর্তিত রেকর্ড করা field",
    missingFields: "রেকর্ড না হওয়া প্রমাণ",
    metricsHidden: "Run-গুলো তুলনাযোগ্য নয় বলে metric ranking লুকানো হয়েছে।",
    export: "প্রমাণ এক্সপোর্ট করুন",
    exportJSON: "Methodology-সহ JSON",
    exportCSV: "Metrics CSV",
    exportReport: "প্রিন্টযোগ্য রিপোর্ট",
    exportDone: "রিসার্চ এক্সপোর্ট ডাউনলোড হয়েছে।",
    exportError: "এক্সপোর্ট তৈরি করা যায়নি।",
    riskNote: "Historical simulation বর্ণনামূলক প্রমাণ; forecast বা investment advice নয়।",
    statusDraft: "খসড়া",
    statusTesting: "পরীক্ষাধীন",
    statusPaper: "Paper monitoring",
    statusArchived: "আর্কাইভ করা",
    evidenceNotEvaluated: "মূল্যায়ন হয়নি",
    evidenceInSample: "In-sample",
    evidenceSelection: "Selection পাস",
    evidenceHoldoutPass: "Locked holdout পাস",
    evidenceHoldoutFail: "Locked holdout ব্যর্থ",
    evidenceInsufficient: "প্রমাণ অপর্যাপ্ত",
  },
  id: {
    eyebrow: "Riset strategi",
    title: "Ruang kerja riset",
    subtitle: "Simpan hipotesis, hasil run yang tidak dapat diubah, dan perbandingan jujur sebelum mempertaruhkan modal.",
    newWorkspace: "Ruang kerja baru",
    workspaceName: "Nama ruang kerja",
    hypothesis: "Hipotesis",
    objective: "Tujuan utama",
    minTrades: "Jumlah trade minimum",
    tags: "Tag (opsional)",
    tagsPlaceholder: "tren, BTC, 1j",
    create: "Buat ruang kerja",
    cancel: "Batal",
    objectiveExpectancy: "Tingkatkan ekspektasi",
    objectiveDrawdown: "Kurangi drawdown",
    objectiveProfitFactor: "Tingkatkan profit factor",
    metricProfitFactor: "Faktor profit",
    metricExpectancy: "Ekspektasi",
    objectiveStability: "Tingkatkan kestabilan",
    loading: "Memuat riset…",
    loadError: "Riset tidak dapat dimuat di perangkat ini.",
    retry: "Coba lagi",
    saveError: "Perubahan belum tersimpan. Riwayat tidak diubah.",
    required: "Tambahkan nama dan hipotesis yang dapat diuji.",
    workspaces: "Ruang kerja",
    unassigned: "Run yang belum dikelompokkan",
    noWorkspaces: "Belum ada ruang kerja riset.",
    noRuns: "Tidak ada run dalam tampilan ini.",
    runCount: "{count} run",
    lastUpdated: "Diperbarui {date}",
    localData: "Riset disimpan untuk akun ini di perangkat. Riwayat asli tetap utuh.",
    baseline: "Baseline",
    setBaseline: "Jadikan baseline",
    temporaryReference: "Run pertama yang dipilih menjadi acuan sementara sampai Anda menetapkan baseline.",
    selectRun: "Pilih run untuk dibandingkan",
    selectedCount: "{count} dipilih",
    maxFour: "Bandingkan maksimal empat run sekaligus.",
    selectTwo: "Pilih dua hingga empat run untuk dibandingkan.",
    includeBaseline: "Pilih baseline tersimpan untuk memakainya sebagai acuan perbandingan.",
    assignTo: "Masukkan run terpilih ke",
    assign: "Masukkan run",
    assigned: "Run dimasukkan tanpa mengubah hasil asli.",
    chooseWorkspace: "Pilih ruang kerja",
    openResult: "Buka hasil",
    openRunDetail: "Buka run riset",
    closeRunDetail: "Tutup run riset",
    moreActions: "Lainnya",
    duplicateAndChange: "Duplikat & ubah",
    preparePaper: "Siapkan untuk paper",
    openFullResult: "Buka hasil lengkap",
    runDetails: "Detail run riset",
    unavailable: "Hasil asli tidak tersedia di perangkat ini; tautan tetapnya masih tersimpan.",
    comparison: "Perbandingan",
    exact: "Sama persis",
    controlled: "Perbandingan terkontrol",
    exploratory: "Perbandingan eksploratif",
    limited: "Bukti terbatas",
    notComparable: "Tidak dapat dibandingkan",
    exactHelp: "Lingkungan dan pengaturan yang tercatat sama. Metrik seharusnya sama kecuali data hasil berbeda.",
    controlledHelp: "Satu variabel tercatat berubah di antara kandidat. Ini mendukung perbandingan terfokus, bukan klaim sebab-akibat.",
    exploratoryHelp: "Beberapa variabel berubah. Gunakan hasil untuk membentuk hipotesis baru, bukan menetapkan satu penyebab.",
    limitedHelp: "Provenans run lama tidak lengkap. Metrik ditampilkan, tetapi perbandingan tidak dapat diverifikasi sepenuhnya.",
    notComparableHelp: "Data pasar atau lingkungan pengujian berbeda. Peringkat performa akan menyesatkan.",
    changedFields: "Kolom tercatat yang berubah",
    missingFields: "Bukti yang tidak tercatat",
    metricsHidden: "Peringkat metrik disembunyikan karena run ini tidak dapat dibandingkan.",
    export: "Ekspor bukti",
    exportJSON: "JSON dengan metodologi",
    exportCSV: "CSV metrik",
    exportReport: "Laporan siap cetak",
    exportDone: "Ekspor riset telah diunduh.",
    exportError: "Ekspor tidak dapat dibuat.",
    riskNote: "Simulasi historis adalah bukti deskriptif, bukan ramalan atau saran investasi.",
    statusDraft: "Draf",
    statusTesting: "Pengujian",
    statusPaper: "Pemantauan paper",
    statusArchived: "Diarsipkan",
    evidenceNotEvaluated: "Belum dievaluasi",
    evidenceInSample: "Dalam sampel",
    evidenceSelection: "Lolos seleksi",
    evidenceHoldoutPass: "Lolos locked holdout",
    evidenceHoldoutFail: "Gagal locked holdout",
    evidenceInsufficient: "Bukti tidak cukup",
  },
  fil: {
    eyebrow: "Pananaliksik ng strategy",
    title: "Research workspace",
    subtitle: "Pagsamahin ang hypotheses, hindi nababagong runs, at tapat na comparisons bago ilagay sa panganib ang kapital.",
    newWorkspace: "Bagong workspace",
    workspaceName: "Pangalan ng workspace",
    hypothesis: "Hypothesis",
    objective: "Pangunahing layunin",
    minTrades: "Pinakamababang trades",
    tags: "Mga tag (opsyonal)",
    tagsPlaceholder: "trend, BTC, 1h",
    create: "Gumawa ng workspace",
    cancel: "Kanselahin",
    objectiveExpectancy: "Taasan ang expectancy",
    objectiveDrawdown: "Bawasan ang drawdown",
    objectiveProfitFactor: "Taasan ang profit factor",
    metricProfitFactor: "Profit factor",
    metricExpectancy: "Expectancy",
    objectiveStability: "Pagandahin ang stability",
    loading: "Nilo-load ang research…",
    loadError: "Hindi ma-load ang research sa device na ito.",
    retry: "Subukan ulit",
    saveError: "Hindi na-save ang pagbabago. Walang binago sa history.",
    required: "Maglagay ng pangalan at hypothesis na maaaring subukan.",
    workspaces: "Mga workspace",
    unassigned: "Hindi nakagrupong runs",
    noWorkspaces: "Wala pang research workspace.",
    noRuns: "Walang run sa view na ito.",
    runCount: "{count} run",
    lastUpdated: "Na-update {date}",
    localData: "Naka-save ang research para sa account na ito sa device. Hindi nagbabago ang orihinal na history.",
    baseline: "Baseline",
    setBaseline: "Gawing baseline",
    temporaryReference: "Pansamantalang reference ang unang piniling run hanggang magtakda ka ng baseline.",
    selectRun: "Pumili ng run para ikumpara",
    selectedCount: "{count} ang napili",
    maxFour: "Hanggang apat na run ang maaaring ikumpara nang sabay.",
    selectTwo: "Pumili ng dalawa hanggang apat na run para ikumpara.",
    includeBaseline: "Piliin ang naka-save na baseline para gamitin itong comparison reference.",
    assignTo: "Ilagay ang napiling runs sa",
    assign: "I-assign ang runs",
    assigned: "Na-assign ang runs nang hindi binabago ang orihinal na results.",
    chooseWorkspace: "Pumili ng workspace",
    openResult: "Buksan ang result",
    openRunDetail: "Buksan ang research run",
    closeRunDetail: "Isara ang research run",
    moreActions: "Higit pa",
    duplicateAndChange: "Kopyahin at baguhin",
    preparePaper: "Ihanda para sa paper",
    openFullResult: "Buksan ang buong result",
    runDetails: "Detalye ng research run",
    unavailable: "Wala sa device na ito ang orihinal na result; napanatili ang hindi nababagong link nito.",
    comparison: "Paghahambing",
    exact: "Eksaktong tugma",
    controlled: "Kontroladong comparison",
    exploratory: "Exploratory comparison",
    limited: "Limitadong ebidensya",
    notComparable: "Hindi maikukumpara",
    exactHelp: "Magkapareho ang recorded environment at settings. Dapat zero ang metric differences maliban kung iba ang result data.",
    controlledHelp: "Isang recorded variable ang nagbago sa candidates. Focused comparison ito, hindi causal claim.",
    exploratoryHelp: "Maraming variable ang nagbago. Gamitin ang result para bumuo ng bagong hypothesis, hindi para tukuyin ang iisang sanhi.",
    limitedHelp: "Kulang ang provenance ng lumang run. Makikita ang metrics pero hindi ganap na mabe-verify ang comparison.",
    notComparableHelp: "Magkaiba ang market data o test environment. Makaliligaw ang performance ranking.",
    changedFields: "Mga recorded field na nagbago",
    missingFields: "Ebidensyang hindi na-record",
    metricsHidden: "Nakatago ang metric ranking dahil hindi maikukumpara ang mga run.",
    export: "I-export ang ebidensya",
    exportJSON: "JSON na may methodology",
    exportCSV: "Metrics CSV",
    exportReport: "Napi-print na ulat",
    exportDone: "Na-download ang research export.",
    exportError: "Hindi magawa ang export.",
    riskNote: "Ang historical simulation ay descriptive evidence, hindi forecast o investment advice.",
    statusDraft: "Draft",
    statusTesting: "Tine-test",
    statusPaper: "Paper monitoring",
    statusArchived: "Naka-archive",
    evidenceNotEvaluated: "Hindi pa nasuri",
    evidenceInSample: "In-sample",
    evidenceSelection: "Pumasa sa selection",
    evidenceHoldoutPass: "Pumasa sa locked holdout",
    evidenceHoldoutFail: "Bumagsak sa locked holdout",
    evidenceInsufficient: "Kulang ang ebidensya",
  },
  "es-419": {
    eyebrow: "Investigación de estrategias",
    title: "Espacio de investigación",
    subtitle: "Reúne hipótesis, ejecuciones inmutables y comparaciones honestas antes de arriesgar capital.",
    newWorkspace: "Nuevo espacio",
    workspaceName: "Nombre del espacio",
    hypothesis: "Hipótesis",
    objective: "Objetivo principal",
    minTrades: "Operaciones mínimas",
    tags: "Etiquetas (opcional)",
    tagsPlaceholder: "tendencia, BTC, 1h",
    create: "Crear espacio",
    cancel: "Cancelar",
    objectiveExpectancy: "Aumentar la expectativa",
    objectiveDrawdown: "Reducir el drawdown",
    objectiveProfitFactor: "Aumentar el factor de ganancia",
    metricProfitFactor: "Factor de beneficio",
    metricExpectancy: "Expectativa",
    objectiveStability: "Mejorar la estabilidad",
    loading: "Cargando investigación…",
    loadError: "No se pudo cargar la investigación en este dispositivo.",
    retry: "Reintentar",
    saveError: "El cambio no se guardó. El historial no se modificó.",
    required: "Agrega un nombre y una hipótesis que se pueda comprobar.",
    workspaces: "Espacios",
    unassigned: "Ejecuciones sin asignar",
    noWorkspaces: "Aún no hay espacios de investigación.",
    noRuns: "No hay ejecuciones en esta vista.",
    runCount: "{count} ejecuciones",
    lastUpdated: "Actualizado {date}",
    localData: "La investigación se guarda para esta cuenta en el dispositivo. El historial original no cambia.",
    baseline: "Línea base",
    setBaseline: "Usar como línea base",
    temporaryReference: "La primera ejecución seleccionada será la referencia temporal hasta que definas una línea base.",
    selectRun: "Seleccionar ejecución para comparar",
    selectedCount: "{count} seleccionadas",
    maxFour: "Compara hasta cuatro ejecuciones a la vez.",
    selectTwo: "Selecciona entre dos y cuatro ejecuciones para comparar.",
    includeBaseline: "Selecciona la línea base guardada para usarla como referencia.",
    assignTo: "Asignar las ejecuciones seleccionadas a",
    assign: "Asignar ejecuciones",
    assigned: "Las ejecuciones se asignaron sin modificar los resultados originales.",
    chooseWorkspace: "Elige un espacio",
    openResult: "Abrir resultado",
    openRunDetail: "Abrir ejecución de investigación",
    closeRunDetail: "Cerrar ejecución de investigación",
    moreActions: "Más",
    duplicateAndChange: "Duplicar y cambiar",
    preparePaper: "Preparar para paper",
    openFullResult: "Abrir resultado completo",
    runDetails: "Detalles de la ejecución",
    unavailable: "El resultado original no está en este dispositivo; su vínculo inmutable se conserva.",
    comparison: "Comparación",
    exact: "Coincidencia exacta",
    controlled: "Comparación controlada",
    exploratory: "Comparación exploratoria",
    limited: "Evidencia limitada",
    notComparable: "No comparables",
    exactHelp: "El entorno y los ajustes registrados coinciden. Las métricas deberían ser iguales salvo que cambien los datos del resultado.",
    controlledHelp: "Cambió una variable registrada entre candidatos. Permite una comparación enfocada, no una afirmación causal.",
    exploratoryHelp: "Cambiaron varias variables. Usa el resultado para formular otra hipótesis, no para atribuir una sola causa.",
    limitedHelp: "Falta procedencia en una ejecución anterior. Se muestran métricas, pero la comparación no puede verificarse por completo.",
    notComparableHelp: "Los datos de mercado o el entorno de prueba son distintos. Clasificar el rendimiento sería engañoso.",
    changedFields: "Campos registrados que cambiaron",
    missingFields: "Evidencia no registrada",
    metricsHidden: "La clasificación de métricas está oculta porque estas ejecuciones no son comparables.",
    export: "Exportar evidencia",
    exportJSON: "JSON con metodología",
    exportCSV: "CSV de métricas",
    exportReport: "Informe para imprimir",
    exportDone: "Se descargó la exportación de investigación.",
    exportError: "No se pudo crear la exportación.",
    riskNote: "Las simulaciones históricas son evidencia descriptiva, no pronósticos ni asesoría de inversión.",
    statusDraft: "Borrador",
    statusTesting: "En prueba",
    statusPaper: "Monitoreo paper",
    statusArchived: "Archivado",
    evidenceNotEvaluated: "Sin evaluar",
    evidenceInSample: "Dentro de muestra",
    evidenceSelection: "Selección aprobada",
    evidenceHoldoutPass: "Holdout bloqueado aprobado",
    evidenceHoldoutFail: "Holdout bloqueado rechazado",
    evidenceInsufficient: "Evidencia insuficiente",
  },
  "pt-BR": {
    eyebrow: "Pesquisa de estratégia",
    title: "Espaço de pesquisa",
    subtitle: "Mantenha hipóteses, execuções imutáveis e comparações honestas juntas antes de arriscar capital.",
    newWorkspace: "Novo espaço",
    workspaceName: "Nome do espaço",
    hypothesis: "Hipótese",
    objective: "Objetivo principal",
    minTrades: "Mínimo de trades",
    tags: "Tags (opcional)",
    tagsPlaceholder: "tendência, BTC, 1h",
    create: "Criar espaço",
    cancel: "Cancelar",
    objectiveExpectancy: "Aumentar a expectativa",
    objectiveDrawdown: "Reduzir o drawdown",
    objectiveProfitFactor: "Aumentar o fator de lucro",
    metricProfitFactor: "Fator de lucro",
    metricExpectancy: "Expectativa",
    objectiveStability: "Melhorar a estabilidade",
    loading: "Carregando pesquisa…",
    loadError: "Não foi possível carregar a pesquisa neste dispositivo.",
    retry: "Tentar novamente",
    saveError: "A alteração não foi salva. O histórico não foi modificado.",
    required: "Adicione um nome e uma hipótese que possa ser testada.",
    workspaces: "Espaços",
    unassigned: "Execuções não atribuídas",
    noWorkspaces: "Ainda não há espaço de pesquisa.",
    noRuns: "Não há execuções nesta visualização.",
    runCount: "{count} execuções",
    lastUpdated: "Atualizado em {date}",
    localData: "A pesquisa fica salva para esta conta no dispositivo. O histórico original não é alterado.",
    baseline: "Linha de base",
    setBaseline: "Definir como linha de base",
    temporaryReference: "A primeira execução selecionada é uma referência temporária até você definir uma linha de base.",
    selectRun: "Selecionar execução para comparar",
    selectedCount: "{count} selecionadas",
    maxFour: "Compare até quatro execuções por vez.",
    selectTwo: "Selecione de duas a quatro execuções para comparar.",
    includeBaseline: "Selecione a linha de base salva para usá-la como referência.",
    assignTo: "Atribuir as execuções selecionadas a",
    assign: "Atribuir execuções",
    assigned: "Execuções atribuídas sem alterar os resultados originais.",
    chooseWorkspace: "Escolha um espaço",
    openResult: "Abrir resultado",
    openRunDetail: "Abrir execução de pesquisa",
    closeRunDetail: "Fechar execução de pesquisa",
    moreActions: "Mais",
    duplicateAndChange: "Duplicar e alterar",
    preparePaper: "Preparar para paper",
    openFullResult: "Abrir resultado completo",
    runDetails: "Detalhes da execução",
    unavailable: "O resultado original não está neste dispositivo; seu vínculo imutável foi preservado.",
    comparison: "Comparação",
    exact: "Correspondência exata",
    controlled: "Comparação controlada",
    exploratory: "Comparação exploratória",
    limited: "Evidência limitada",
    notComparable: "Não comparável",
    exactHelp: "O ambiente e os ajustes registrados coincidem. As métricas devem ser iguais, a menos que os dados do resultado sejam diferentes.",
    controlledHelp: "Uma variável registrada mudou entre os candidatos. Isso permite uma comparação focada, não uma conclusão causal.",
    exploratoryHelp: "Várias variáveis mudaram. Use o resultado para formular outra hipótese, não para indicar uma única causa.",
    limitedHelp: "Faltam dados de procedência em uma execução antiga. As métricas aparecem, mas a comparação não pode ser totalmente verificada.",
    notComparableHelp: "Os dados de mercado ou o ambiente de teste são diferentes. Classificar o desempenho seria enganoso.",
    changedFields: "Campos registrados que mudaram",
    missingFields: "Evidência não registrada",
    metricsHidden: "A classificação das métricas está oculta porque estas execuções não são comparáveis.",
    export: "Exportar evidência",
    exportJSON: "JSON com metodologia",
    exportCSV: "CSV de métricas",
    exportReport: "Relatório para impressão",
    exportDone: "A exportação da pesquisa foi baixada.",
    exportError: "Não foi possível criar a exportação.",
    riskNote: "Simulações históricas são evidências descritivas, não previsões nem recomendações de investimento.",
    statusDraft: "Rascunho",
    statusTesting: "Em teste",
    statusPaper: "Monitoramento paper",
    statusArchived: "Arquivado",
    evidenceNotEvaluated: "Não avaliado",
    evidenceInSample: "Na amostra",
    evidenceSelection: "Seleção aprovada",
    evidenceHoldoutPass: "Holdout bloqueado aprovado",
    evidenceHoldoutFail: "Holdout bloqueado reprovado",
    evidenceInsufficient: "Evidência insuficiente",
  },
  ja: {
    eyebrow: "戦略リサーチ",
    title: "リサーチワークスペース",
    subtitle: "資金をリスクにさらす前に、仮説、変更できない実行結果、誠実な比較をまとめて管理します。",
    newWorkspace: "新しいワークスペース",
    workspaceName: "ワークスペース名",
    hypothesis: "仮説",
    objective: "主な目的",
    minTrades: "最低取引数",
    tags: "タグ（任意）",
    tagsPlaceholder: "トレンド、BTC、1時間足",
    create: "ワークスペースを作成",
    cancel: "キャンセル",
    objectiveExpectancy: "期待値を上げる",
    objectiveDrawdown: "ドローダウンを抑える",
    objectiveProfitFactor: "プロフィットファクターを上げる",
    metricProfitFactor: "プロフィットファクター",
    metricExpectancy: "期待値",
    objectiveStability: "安定性を高める",
    loading: "リサーチを読み込み中…",
    loadError: "この端末でリサーチを読み込めませんでした。",
    retry: "再試行",
    saveError: "変更は保存されませんでした。履歴は変更されていません。",
    required: "名前と検証可能な仮説を入力してください。",
    workspaces: "ワークスペース",
    unassigned: "未分類の実行結果",
    noWorkspaces: "リサーチワークスペースはまだありません。",
    noRuns: "この表示には実行結果がありません。",
    runCount: "{count}件の実行",
    lastUpdated: "{date}に更新",
    localData: "リサーチはこの端末のアカウント領域に保存されます。元の履歴は変更されません。",
    baseline: "ベースライン",
    setBaseline: "ベースラインに設定",
    temporaryReference: "ベースラインを設定するまでは、最初に選んだ実行結果が一時的な基準になります。",
    selectRun: "比較する実行結果を選択",
    selectedCount: "{count}件を選択中",
    maxFour: "一度に最大4件まで比較できます。",
    selectTwo: "比較する実行結果を2〜4件選択してください。",
    includeBaseline: "保存済みのベースラインを選択すると比較基準として使用できます。",
    assignTo: "選択した実行結果の移動先",
    assign: "実行結果を割り当てる",
    assigned: "元の結果を変更せずに割り当てました。",
    chooseWorkspace: "ワークスペースを選択",
    openResult: "結果を開く",
    openRunDetail: "リサーチ実行を開く",
    closeRunDetail: "リサーチ実行を閉じる",
    moreActions: "その他",
    duplicateAndChange: "複製して変更",
    preparePaper: "ペーパー運用を準備",
    openFullResult: "結果をすべて開く",
    runDetails: "リサーチ実行の詳細",
    unavailable: "元の結果はこの端末にありませんが、変更できないリンクは保持されています。",
    comparison: "比較",
    exact: "完全一致",
    controlled: "統制された比較",
    exploratory: "探索的な比較",
    limited: "証拠が限定的",
    notComparable: "比較不可",
    exactHelp: "記録された環境と設定が一致しています。結果データが異ならない限り、指標の差はないはずです。",
    controlledHelp: "候補間で記録済みの変数が1つ変わっています。焦点を絞った比較はできますが、因果関係は証明できません。",
    exploratoryHelp: "複数の変数が変わっています。単一の原因を決めず、新しい仮説づくりに使ってください。",
    limitedHelp: "以前の実行結果には来歴情報が不足しています。指標は表示しますが、比較を完全には検証できません。",
    notComparableHelp: "市場データまたはテスト環境が異なります。成績を順位付けすると誤解を招きます。",
    changedFields: "変更された記録項目",
    missingFields: "記録されていない証拠",
    metricsHidden: "比較できない実行結果のため、指標の順位表示を隠しています。",
    export: "証拠をエクスポート",
    exportJSON: "手法情報付きJSON",
    exportCSV: "指標CSV",
    exportReport: "印刷用レポート",
    exportDone: "リサーチのエクスポートをダウンロードしました。",
    exportError: "エクスポートを作成できませんでした。",
    riskNote: "過去データのシミュレーションは記述的な証拠であり、予測や投資助言ではありません。",
    statusDraft: "下書き",
    statusTesting: "テスト中",
    statusPaper: "ペーパー監視中",
    statusArchived: "アーカイブ済み",
    evidenceNotEvaluated: "未評価",
    evidenceInSample: "インサンプル",
    evidenceSelection: "選定通過",
    evidenceHoldoutPass: "固定ホールドアウト合格",
    evidenceHoldoutFail: "固定ホールドアウト不合格",
    evidenceInsufficient: "証拠不足",
  },
  ko: {
    eyebrow: "전략 연구",
    title: "리서치 워크스페이스",
    subtitle: "자본을 위험에 노출하기 전에 가설, 변경 불가능한 실행 결과, 정직한 비교를 함께 관리하세요.",
    newWorkspace: "새 워크스페이스",
    workspaceName: "워크스페이스 이름",
    hypothesis: "가설",
    objective: "주요 목표",
    minTrades: "최소 거래 수",
    tags: "태그(선택)",
    tagsPlaceholder: "추세, BTC, 1시간",
    create: "워크스페이스 만들기",
    cancel: "취소",
    objectiveExpectancy: "기대값 높이기",
    objectiveDrawdown: "드로다운 줄이기",
    objectiveProfitFactor: "프로핏 팩터 높이기",
    metricProfitFactor: "수익 계수",
    metricExpectancy: "기대값",
    objectiveStability: "안정성 높이기",
    loading: "리서치 불러오는 중…",
    loadError: "이 기기에서 리서치를 불러오지 못했습니다.",
    retry: "다시 시도",
    saveError: "변경 사항이 저장되지 않았습니다. 기록은 바뀌지 않았습니다.",
    required: "이름과 검증 가능한 가설을 모두 입력하세요.",
    workspaces: "워크스페이스",
    unassigned: "분류되지 않은 실행 결과",
    noWorkspaces: "아직 리서치 워크스페이스가 없습니다.",
    noRuns: "이 화면에 실행 결과가 없습니다.",
    runCount: "실행 {count}개",
    lastUpdated: "{date} 업데이트",
    localData: "리서치는 이 기기의 해당 계정 영역에 저장되며 원본 기록은 변경되지 않습니다.",
    baseline: "베이스라인",
    setBaseline: "베이스라인으로 설정",
    temporaryReference: "베이스라인을 설정하기 전에는 첫 번째 선택 결과가 임시 기준입니다.",
    selectRun: "비교할 실행 결과 선택",
    selectedCount: "{count}개 선택됨",
    maxFour: "한 번에 최대 4개까지 비교할 수 있습니다.",
    selectTwo: "비교할 실행 결과를 2~4개 선택하세요.",
    includeBaseline: "저장된 베이스라인을 선택하면 비교 기준으로 사용됩니다.",
    assignTo: "선택한 실행 결과를 다음으로 이동",
    assign: "실행 결과 배정",
    assigned: "원본 결과를 바꾸지 않고 배정했습니다.",
    chooseWorkspace: "워크스페이스 선택",
    openResult: "결과 열기",
    openRunDetail: "리서치 실행 열기",
    closeRunDetail: "리서치 실행 닫기",
    moreActions: "더보기",
    duplicateAndChange: "복제 후 변경",
    preparePaper: "페이퍼 준비",
    openFullResult: "전체 결과 열기",
    runDetails: "리서치 실행 세부 정보",
    unavailable: "원본 결과가 이 기기에 없지만 변경 불가능한 링크는 보존되어 있습니다.",
    comparison: "비교",
    exact: "완전 일치",
    controlled: "통제 비교",
    exploratory: "탐색 비교",
    limited: "제한된 근거",
    notComparable: "비교 불가",
    exactHelp: "기록된 환경과 설정이 같습니다. 결과 데이터가 다르지 않다면 지표 차이는 없어야 합니다.",
    controlledHelp: "후보 간 기록된 변수 하나가 바뀌었습니다. 집중 비교는 가능하지만 인과관계를 뜻하지는 않습니다.",
    exploratoryHelp: "여러 변수가 바뀌었습니다. 하나의 원인을 단정하지 말고 새 가설을 만드는 데 사용하세요.",
    limitedHelp: "이전 실행 결과의 출처 정보가 부족합니다. 지표는 표시하지만 비교를 완전히 검증할 수 없습니다.",
    notComparableHelp: "시장 데이터나 테스트 환경이 다릅니다. 성과 순위를 매기면 오해를 부를 수 있습니다.",
    changedFields: "변경된 기록 필드",
    missingFields: "기록되지 않은 근거",
    metricsHidden: "비교할 수 없는 실행 결과이므로 지표 순위를 숨겼습니다.",
    export: "근거 내보내기",
    exportJSON: "방법론 포함 JSON",
    exportCSV: "지표 CSV",
    exportReport: "인쇄용 보고서",
    exportDone: "리서치 내보내기를 다운로드했습니다.",
    exportError: "내보내기 파일을 만들지 못했습니다.",
    riskNote: "과거 데이터 시뮬레이션은 설명적 근거일 뿐 예측이나 투자 조언이 아닙니다.",
    statusDraft: "초안",
    statusTesting: "테스트 중",
    statusPaper: "페이퍼 모니터링",
    statusArchived: "보관됨",
    evidenceNotEvaluated: "미평가",
    evidenceInSample: "인샘플",
    evidenceSelection: "선정 통과",
    evidenceHoldoutPass: "잠금 홀드아웃 통과",
    evidenceHoldoutFail: "잠금 홀드아웃 실패",
    evidenceInsufficient: "근거 부족",
  },
  ru: {
    eyebrow: "Исследование стратегии",
    title: "Рабочее пространство исследований",
    subtitle: "Храните гипотезы, неизменяемые запуски и честные сравнения вместе, прежде чем рисковать капиталом.",
    newWorkspace: "Новое пространство",
    workspaceName: "Название пространства",
    hypothesis: "Гипотеза",
    objective: "Главная цель",
    minTrades: "Минимум сделок",
    tags: "Теги (необязательно)",
    tagsPlaceholder: "тренд, BTC, 1ч",
    create: "Создать пространство",
    cancel: "Отмена",
    objectiveExpectancy: "Повысить матожидание",
    objectiveDrawdown: "Снизить просадку",
    objectiveProfitFactor: "Повысить профит-фактор",
    metricProfitFactor: "Профит-фактор",
    metricExpectancy: "Математическое ожидание",
    objectiveStability: "Повысить стабильность",
    loading: "Загрузка исследования…",
    loadError: "Не удалось загрузить исследование на этом устройстве.",
    retry: "Повторить",
    saveError: "Изменение не сохранено. История не была изменена.",
    required: "Добавьте название и проверяемую гипотезу.",
    workspaces: "Пространства",
    unassigned: "Нераспределённые запуски",
    noWorkspaces: "Рабочих пространств исследования пока нет.",
    noRuns: "В этом разделе нет запусков.",
    runCount: "Запусков: {count}",
    lastUpdated: "Обновлено {date}",
    localData: "Исследование хранится для этой учётной записи на устройстве. Исходная история не меняется.",
    baseline: "Базовый запуск",
    setBaseline: "Сделать базовым",
    temporaryReference: "Первый выбранный запуск служит временной точкой отсчёта, пока вы не зададите базовый.",
    selectRun: "Выбрать запуск для сравнения",
    selectedCount: "Выбрано: {count}",
    maxFour: "Сравнивайте до четырёх запусков одновременно.",
    selectTwo: "Выберите от двух до четырёх запусков для сравнения.",
    includeBaseline: "Выберите сохранённый базовый запуск, чтобы использовать его как точку отсчёта.",
    assignTo: "Перенести выбранные запуски в",
    assign: "Распределить запуски",
    assigned: "Запуски распределены без изменения исходных результатов.",
    chooseWorkspace: "Выберите пространство",
    openResult: "Открыть результат",
    openRunDetail: "Открыть исследовательский запуск",
    closeRunDetail: "Закрыть исследовательский запуск",
    moreActions: "Ещё",
    duplicateAndChange: "Создать копию и изменить",
    preparePaper: "Подготовить к paper-режиму",
    openFullResult: "Открыть полный результат",
    runDetails: "Детали исследовательского запуска",
    unavailable: "Исходного результата нет на этом устройстве; его неизменяемая ссылка сохранена.",
    comparison: "Сравнение",
    exact: "Полное совпадение",
    controlled: "Контролируемое сравнение",
    exploratory: "Исследовательское сравнение",
    limited: "Ограниченные данные",
    notComparable: "Несопоставимо",
    exactHelp: "Записанные среда и настройки совпадают. Метрики должны совпасть, если данные результата не отличаются.",
    controlledHelp: "Между кандидатами изменилась одна записанная переменная. Это сфокусированное сравнение, но не доказательство причины.",
    exploratoryHelp: "Изменилось несколько переменных. Используйте результат для новой гипотезы, а не для вывода об одной причине.",
    limitedHelp: "У старого запуска не хватает данных о происхождении. Метрики показаны, но сравнение нельзя проверить полностью.",
    notComparableHelp: "Рыночные данные или тестовая среда различаются. Рейтинг результатов вводил бы в заблуждение.",
    changedFields: "Изменившиеся записанные поля",
    missingFields: "Незаписанные данные",
    metricsHidden: "Рейтинг метрик скрыт, потому что эти запуски несопоставимы.",
    export: "Экспортировать данные",
    exportJSON: "JSON с методологией",
    exportCSV: "CSV с метриками",
    exportReport: "Отчёт для печати",
    exportDone: "Экспорт исследования загружен.",
    exportError: "Не удалось создать экспорт.",
    riskNote: "Историческая симуляция — описательные данные, а не прогноз или инвестиционная рекомендация.",
    statusDraft: "Черновик",
    statusTesting: "Тестирование",
    statusPaper: "Paper-мониторинг",
    statusArchived: "В архиве",
    evidenceNotEvaluated: "Не оценено",
    evidenceInSample: "В выборке",
    evidenceSelection: "Отбор пройден",
    evidenceHoldoutPass: "Закрытый holdout пройден",
    evidenceHoldoutFail: "Закрытый holdout не пройден",
    evidenceInsufficient: "Недостаточно данных",
  },
  tr: {
    eyebrow: "Strateji araştırması",
    title: "Araştırma çalışma alanı",
    subtitle: "Sermayeyi riske atmadan önce hipotezleri, değiştirilemez çalıştırmaları ve dürüst karşılaştırmaları bir arada tutun.",
    newWorkspace: "Yeni çalışma alanı",
    workspaceName: "Çalışma alanı adı",
    hypothesis: "Hipotez",
    objective: "Ana hedef",
    minTrades: "Minimum işlem",
    tags: "Etiketler (isteğe bağlı)",
    tagsPlaceholder: "trend, BTC, 1s",
    create: "Çalışma alanı oluştur",
    cancel: "İptal",
    objectiveExpectancy: "Beklentiyi artır",
    objectiveDrawdown: "Düşüşü azalt",
    objectiveProfitFactor: "Kâr faktörünü artır",
    metricProfitFactor: "Kâr faktörü",
    metricExpectancy: "Beklenti",
    objectiveStability: "İstikrarı artır",
    loading: "Araştırma yükleniyor…",
    loadError: "Araştırma bu cihazda yüklenemedi.",
    retry: "Tekrar dene",
    saveError: "Değişiklik kaydedilmedi. Geçmiş değiştirilmedi.",
    required: "Bir ad ve test edilebilir hipotez ekleyin.",
    workspaces: "Çalışma alanları",
    unassigned: "Atanmamış çalıştırmalar",
    noWorkspaces: "Henüz araştırma çalışma alanı yok.",
    noRuns: "Bu görünümde çalıştırma yok.",
    runCount: "{count} çalıştırma",
    lastUpdated: "{date} tarihinde güncellendi",
    localData: "Araştırma bu hesap için cihazda saklanır. Orijinal geçmiş değişmez.",
    baseline: "Temel sonuç",
    setBaseline: "Temel sonuç yap",
    temporaryReference: "Bir temel sonuç belirleyene kadar ilk seçilen çalıştırma geçici referanstır.",
    selectRun: "Karşılaştırma için çalıştırma seç",
    selectedCount: "{count} seçildi",
    maxFour: "Aynı anda en fazla dört çalıştırmayı karşılaştırın.",
    selectTwo: "Karşılaştırmak için iki ile dört çalıştırma seçin.",
    includeBaseline: "Kayıtlı temel sonucu karşılaştırma referansı olarak kullanmak için seçin.",
    assignTo: "Seçilen çalıştırmaları şuraya ata",
    assign: "Çalıştırmaları ata",
    assigned: "Çalıştırmalar orijinal sonuçlar değiştirilmeden atandı.",
    chooseWorkspace: "Çalışma alanı seç",
    openResult: "Sonucu aç",
    openRunDetail: "Araştırma çalışmasını aç",
    closeRunDetail: "Araştırma çalışmasını kapat",
    moreActions: "Diğer",
    duplicateAndChange: "Kopyala ve değiştir",
    preparePaper: "Paper için hazırla",
    openFullResult: "Tam sonucu aç",
    runDetails: "Araştırma çalışması ayrıntıları",
    unavailable: "Orijinal sonuç bu cihazda yok; değiştirilemez bağlantısı korunuyor.",
    comparison: "Karşılaştırma",
    exact: "Tam eşleşme",
    controlled: "Kontrollü karşılaştırma",
    exploratory: "Keşif amaçlı karşılaştırma",
    limited: "Sınırlı kanıt",
    notComparable: "Karşılaştırılamaz",
    exactHelp: "Kaydedilen ortam ve ayarlar eşleşiyor. Sonuç verileri farklı değilse metrik farkları sıfır olmalıdır.",
    controlledHelp: "Adaylar arasında kaydedilmiş tek bir değişken farklı. Bu odaklı karşılaştırma sağlar, nedensellik kanıtlamaz.",
    exploratoryHelp: "Birden fazla değişken farklı. Sonucu tek neden bulmak için değil, yeni hipotez kurmak için kullanın.",
    limitedHelp: "Eski çalıştırmanın kaynak bilgileri eksik. Metrikler gösterilir ancak karşılaştırma tam doğrulanamaz.",
    notComparableHelp: "Piyasa verisi veya test ortamı farklı. Performans sıralaması yanıltıcı olur.",
    changedFields: "Değişen kayıtlı alanlar",
    missingFields: "Kaydedilmemiş kanıt",
    metricsHidden: "Bu çalıştırmalar karşılaştırılamadığı için metrik sıralaması gizlendi.",
    export: "Kanıtı dışa aktar",
    exportJSON: "Yöntem bilgili JSON",
    exportCSV: "Metrik CSV",
    exportReport: "Yazdırılabilir rapor",
    exportDone: "Araştırma dışa aktarımı indirildi.",
    exportError: "Dışa aktarım oluşturulamadı.",
    riskNote: "Geçmiş simülasyonlar betimleyici kanıttır; tahmin veya yatırım tavsiyesi değildir.",
    statusDraft: "Taslak",
    statusTesting: "Test ediliyor",
    statusPaper: "Paper izleme",
    statusArchived: "Arşivlendi",
    evidenceNotEvaluated: "Değerlendirilmedi",
    evidenceInSample: "Örneklem içi",
    evidenceSelection: "Seçimi geçti",
    evidenceHoldoutPass: "Kilitli holdout geçti",
    evidenceHoldoutFail: "Kilitli holdout başarısız",
    evidenceInsufficient: "Kanıt yetersiz",
  },
  uk: {
    eyebrow: "Дослідження стратегії",
    title: "Робочий простір досліджень",
    subtitle: "Зберігайте гіпотези, незмінні запуски та чесні порівняння разом, перш ніж ризикувати капіталом.",
    newWorkspace: "Новий простір",
    workspaceName: "Назва простору",
    hypothesis: "Гіпотеза",
    objective: "Головна мета",
    minTrades: "Мінімум угод",
    tags: "Теги (необов’язково)",
    tagsPlaceholder: "тренд, BTC, 1г",
    create: "Створити простір",
    cancel: "Скасувати",
    objectiveExpectancy: "Підвищити очікування",
    objectiveDrawdown: "Зменшити просадку",
    objectiveProfitFactor: "Підвищити профіт-фактор",
    metricProfitFactor: "Профіт-фактор",
    metricExpectancy: "Математичне очікування",
    objectiveStability: "Підвищити стабільність",
    loading: "Завантаження дослідження…",
    loadError: "Не вдалося завантажити дослідження на цьому пристрої.",
    retry: "Спробувати ще раз",
    saveError: "Зміну не збережено. Історію не змінено.",
    required: "Додайте назву та гіпотезу, яку можна перевірити.",
    workspaces: "Робочі простори",
    unassigned: "Нерозподілені запуски",
    noWorkspaces: "Робочих просторів дослідження ще немає.",
    noRuns: "У цьому розділі немає запусків.",
    runCount: "Запусків: {count}",
    lastUpdated: "Оновлено {date}",
    localData: "Дослідження зберігається для цього облікового запису на пристрої. Початкова історія не змінюється.",
    baseline: "Базовий запуск",
    setBaseline: "Зробити базовим",
    temporaryReference: "Перший вибраний запуск є тимчасовим орієнтиром, доки ви не задасте базовий.",
    selectRun: "Вибрати запуск для порівняння",
    selectedCount: "Вибрано: {count}",
    maxFour: "Порівнюйте до чотирьох запусків одночасно.",
    selectTwo: "Виберіть від двох до чотирьох запусків для порівняння.",
    includeBaseline: "Виберіть збережений базовий запуск, щоб використати його як орієнтир.",
    assignTo: "Перенести вибрані запуски до",
    assign: "Розподілити запуски",
    assigned: "Запуски розподілено без зміни початкових результатів.",
    chooseWorkspace: "Виберіть простір",
    openResult: "Відкрити результат",
    openRunDetail: "Відкрити дослідницький запуск",
    closeRunDetail: "Закрити дослідницький запуск",
    moreActions: "Ще",
    duplicateAndChange: "Створити копію та змінити",
    preparePaper: "Підготувати до paper-режиму",
    openFullResult: "Відкрити повний результат",
    runDetails: "Деталі дослідницького запуску",
    unavailable: "Початкового результату немає на цьому пристрої; його незмінне посилання збережено.",
    comparison: "Порівняння",
    exact: "Повний збіг",
    controlled: "Контрольоване порівняння",
    exploratory: "Дослідницьке порівняння",
    limited: "Обмежені докази",
    notComparable: "Непорівнювані",
    exactHelp: "Записані середовище й налаштування збігаються. Метрики мають збігатися, якщо дані результату не відрізняються.",
    controlledHelp: "Між кандидатами змінилася одна записана змінна. Це сфокусоване порівняння, але не доказ причини.",
    exploratoryHelp: "Змінилося кілька змінних. Використайте результат для нової гіпотези, а не для висновку про одну причину.",
    limitedHelp: "Старому запуску бракує даних про походження. Метрики показано, але порівняння неможливо повністю перевірити.",
    notComparableHelp: "Ринкові дані або тестове середовище відрізняються. Рейтинг результатів вводив би в оману.",
    changedFields: "Змінені записані поля",
    missingFields: "Незаписані докази",
    metricsHidden: "Рейтинг метрик приховано, оскільки ці запуски непорівнювані.",
    export: "Експортувати докази",
    exportJSON: "JSON із методологією",
    exportCSV: "CSV із метриками",
    exportReport: "Звіт для друку",
    exportDone: "Експорт дослідження завантажено.",
    exportError: "Не вдалося створити експорт.",
    riskNote: "Історична симуляція — описові докази, а не прогноз чи інвестиційна порада.",
    statusDraft: "Чернетка",
    statusTesting: "Тестування",
    statusPaper: "Paper-моніторинг",
    statusArchived: "В архіві",
    evidenceNotEvaluated: "Не оцінено",
    evidenceInSample: "У вибірці",
    evidenceSelection: "Відбір пройдено",
    evidenceHoldoutPass: "Закритий holdout пройдено",
    evidenceHoldoutFail: "Закритий holdout не пройдено",
    evidenceInsufficient: "Недостатньо доказів",
  },
};

export function researchT(
  locale: Locale,
  key: ResearchTranslationKey,
  parameters: Record<string, string | number> = {},
): string {
  const template = researchTranslations[locale]?.[key] || en[key];
  return Object.entries(parameters).reduce(
    (value, [name, replacement]) => value.split(`{${name}}`).join(String(replacement)),
    template,
  );
}

export const researchTranslationKeys = Object.freeze(
  Object.keys(en) as ResearchTranslationKey[],
);

export function validateResearchTranslations(): string[] {
  const issues: string[] = [];
  for (const locale of locales) {
    for (const key of researchTranslationKeys) {
      const value = researchTranslations[locale]?.[key];
      if (!value?.trim()) issues.push(`${locale}:${key}`);
    }
  }
  return issues;
}
